/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { addDays, format, isAfter, parseISO, roundToNearestMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { capitalize, groupBy, startCase } from "lodash";

import { type HiringFeedPlacement } from "../HiringFeed/api/hiringFeed.schema";
import { type InterviewAvailability } from "./api/useGetInterviewAvailability";
import { type PlacementDetail } from "./api/useGetPlacement";
import {
  CompletedPlacementApplicationStatuses,
  type PlacementData,
  type PlacementIncludedInterview,
  type TagInclude,
} from "./types/fetchPlacements.schema";

export const DEFAULT_DISTANCE_FILTER_IN_MILES = 150;

export const showApplicationsStorageKey = "jobs-show-applications";

export function applicationActiveSectionStorageKey(title: string) {
  return `placements-accordion-section-${title.toLowerCase().replace(" ", "-")}-expanded`;
}

export function hasAppliedToPlacement(placement?: PlacementDetail) {
  return (placement?.applications?.length ?? 0) > 0;
}

export function isApplicationCompleted(placement?: PlacementDetail) {
  return Boolean(
    placement?.applications?.[0]?.attributes?.status &&
      CompletedPlacementApplicationStatuses.includes(placement.applications[0].attributes.status)
  );
}

export function interviewRequired(placement?: PlacementDetail) {
  return Boolean(
    placement?.interviewSettings?.attributes?.scheduleInterviews &&
      placement.interviewSettings.attributes.availability?.length > 0
  );
}

export function hasBookedInterview(placement?: PlacementDetail) {
  const hasAtLeastOneApplication = (placement?.applications?.length ?? 0) > 0;
  const isInterviewBooked =
    placement?.interviews?.some(
      (interview) => interview.attributes.status === InterviewStatus.BOOKED
    ) ?? false;
  return hasAtLeastOneApplication && isInterviewBooked;
}

export function hasBookedInterviewInFuture(placement?: PlacementDetail) {
  const hasAtLeastOneApplication = (placement?.applications?.length ?? 0) > 0;
  const bookedInterview = placement?.interviews?.find(
    (interview) =>
      interview.attributes.status === InterviewStatus.BOOKED &&
      isAfter(parseISO(interview.attributes.start), new Date())
  );
  return hasAtLeastOneApplication && isDefined(bookedInterview);
}

export function hasAttendedInterview(placement?: PlacementDetail) {
  const hasAtLeastOneApplication = (placement?.applications?.length ?? 0) > 0;
  const isInterviewAttended =
    placement?.interviews?.some(
      (interview) => interview.attributes.status === InterviewStatus.ATTENDANCE_CONFIRMED
    ) ?? false;
  return hasAtLeastOneApplication && isInterviewAttended;
}

export function hasCancelledInterview(placement?: PlacementDetail) {
  return (
    interviewRequired(placement) &&
    hasAppliedToPlacement(placement) &&
    placement?.interviews?.some(
      (interview) => interview.attributes.status === InterviewStatus.CANCELLED
    )
  );
}

interface FormatPayRangeArguments {
  payRange: PlacementData["attributes"]["payRange"] | undefined;
  showPerHour: boolean;
  withFractionDigits?: boolean;
}

export function formatPayRangeLabel({
  payRange,
  showPerHour,
  withFractionDigits,
}: FormatPayRangeArguments) {
  const prefix = getPayRangeLabelPrefix(payRange);
  const suffix = formatPayRange({ payRange, showPerHour, withFractionDigits });

  if (!suffix) {
    return undefined;
  }

  if (!prefix) {
    return suffix;
  }

  return `${prefix} ${suffix}`;
}

export function getPayRangeLabelPrefix(
  payRange: PlacementData["attributes"]["payRange"] | undefined
) {
  if (!payRange || (!payRange.minPay && !payRange.maxPay)) {
    return undefined;
  }

  if (
    isDefined(payRange.minPay) &&
    isDefined(payRange.maxPay) &&
    payRange.minPay.amountInMinorUnits !== payRange.maxPay.amountInMinorUnits
  ) {
    return undefined;
  }

  if (isDefined(payRange.minPay) && !isDefined(payRange.maxPay)) {
    return "From";
  }

  if (isDefined(payRange.maxPay)) {
    return "Up to";
  }

  return "Pay";
}

export function formatPayRange({
  payRange,
  showPerHour,
  withFractionDigits = false,
}: FormatPayRangeArguments) {
  if (!payRange || (!payRange.minPay && !payRange.maxPay)) {
    return undefined;
  }

  if (
    isDefined(payRange.minPay) &&
    isDefined(payRange.maxPay) &&
    payRange.minPay.amountInMinorUnits !== payRange.maxPay.amountInMinorUnits
  ) {
    return `~${formatCentsAsUsd(payRange.minPay.amountInMinorUnits, {
      maximumFractionDigits: withFractionDigits ? 2 : 0,
    })} - ${formatCentsAsUsd(payRange.maxPay.amountInMinorUnits, {
      maximumFractionDigits: withFractionDigits ? 2 : 0,
    })}${showPerHour ? " /hr" : ""}`;
  }

  const singlePayAmount =
    payRange.minPay?.amountInMinorUnits ?? payRange.maxPay?.amountInMinorUnits;

  if (!isDefined(singlePayAmount)) {
    return undefined;
  }

  return `${formatCentsAsUsd(singlePayAmount)}${showPerHour ? " /hr" : ""}`;
}

export function getPayRangeText(
  minMaxPayEstimate: { min: number; max: number } | undefined,
  payRange: HiringFeedPlacement["attributes"]["payRange"],
  withFractionDigits = false
) {
  if (isDefined(minMaxPayEstimate)) {
    return minMaxPayEstimate.min === minMaxPayEstimate.max
      ? formatCentsAsUsd(minMaxPayEstimate.min)
      : `${formatCentsAsUsd(minMaxPayEstimate.min)} - ${formatCentsAsUsd(minMaxPayEstimate.max)}`;
  }

  return formatPayRange({ payRange, showPerHour: false, withFractionDigits });
}

export const SUPPORTED_LICENSES_ORDER = new Set([
  "Lead Teacher",
  "Director",
  "Teacher Assistant",
  "Dental Hygienist",
  "Dental Assistant",
  "RN",
  "LVN",
  "CNA",
  "CHHA",
  "HHA",
  "Caregiver",
]);

export const CHILD_CARE_LICENSE_TYPES = new Set(["Lead Teacher", "Director", "Teacher Assistant"]);

export function hasChildcareQualification(workerQualifications: string[]): boolean {
  return workerQualifications.some((qualification) => CHILD_CARE_LICENSE_TYPES.has(qualification));
}

export function getFormattedInterviewDate(interview: PlacementIncludedInterview, timezone: string) {
  const startDateInTimezone = utcToZonedTime(interview.attributes.start, timezone);
  const endDateInTimezone = utcToZonedTime(interview.attributes.end, timezone);

  return `${format(startDateInTimezone, "MMM d, yyyy 'from' h:mm a")} - ${format(
    endDateInTimezone,
    "h:mm a"
  )}`;
}

// eslint-disable-next-line max-params
export function getApplyButtonLabel(
  hasApplied: boolean,
  applicationCompleted: boolean,
  requiredInterview: boolean,
  bookedInterview: boolean,
  hasOpenHouse: boolean,
  isWaitlist: boolean,
  isJobRequestEnabled: boolean
) {
  if (hasApplied) {
    if (applicationCompleted) {
      return "Applied";
    }

    if ((requiredInterview || hasOpenHouse) && bookedInterview) {
      return "Booked interview";
    }

    return "Applied";
  }

  if (isWaitlist) {
    return "Join waitlist";
  }

  return isJobRequestEnabled ? "Request to interview" : "Apply to job";
}

export function getCurrentWorkerTrialsDateRange() {
  const now = new Date();
  // round to prevent from triggering change detection on every render, which was spamming the API
  const nowRounded = roundToNearestMinutes(now, {
    nearestTo: 30,
    roundingMethod: "ceil",
  });

  return {
    start: nowRounded.toISOString(),
    end: addDays(nowRounded, 7).toISOString(),
  };
}

export function getPlacementCardType(
  searchParams: URLSearchParams
): "open-house" | "interview-days" | undefined {
  const cardType = searchParams.get("cardType");

  if (cardType === "open-house" || cardType === "interview-days") {
    return cardType;
  }

  return undefined;
}

export function hasOnlyOpenHouseAvailable(interviewAvailability: InterviewAvailability) {
  const types = new Set(interviewAvailability.map((slot) => slot.eventType).filter(isDefined));
  return types.size === 1 && types.has("open-house");
}

export function doesPlacementHasOpenHouse(placementDetails?: PlacementDetail): boolean {
  return (
    isDefined(placementDetails?.hiringEvents) &&
    placementDetails.hiringEvents.some(
      (hiringEvent) =>
        hiringEvent.type === "hiring-event" && hiringEvent.attributes.type === "OpenHouse"
    )
  );
}

/**
 * Determines if a placement requirement should be shown to the user.
 * Requirements are shown if they are:
 * 1. Invalid AND visible to HCP (standard behavior), OR
 * 2. the requirement is not the legacy document requirement (when specified)
 */
export function shouldShowRequirement(
  requirement: {
    attributes: { isValid: boolean };
    relationships: {
      requirement: {
        data: {
          id: string;
          attributes: { visibleToHCP?: boolean };
        };
      };
    };
  },
  legacyScreeningDocumentRequirementId?: string
): boolean {
  const { isValid } = requirement.attributes;
  if (isValid) {
    return false;
  }

  const { id, attributes: { visibleToHCP = false } = {} } =
    requirement.relationships.requirement.data;

  // Exclude the legacy document requirement
  if (legacyScreeningDocumentRequirementId && id === legacyScreeningDocumentRequirementId) {
    return false;
  }

  return visibleToHCP;
}

export interface FormatTagGroupOptions {
  sortTags?: boolean;
  defaultGroupName?: string;
  includeValidTagsFlag?: boolean;
}

export interface FormattedTagGroup {
  groupLabel: string;
  formattedTags: string;
  hasValidTags?: boolean;
}

// TODO: Refactor this function once we replace the generic tag implementation with PlacementTags
// https://linear.app/clipboardhealth/issue/GRO-1040/move-tags-to-placementtags-and-includ-a-workplace-type-instead-of
// Sort tags by order, if available, with an alphabetical fallback
function sortTagsByOrderThenAlphabetically<T extends { name: string; order?: number }>(
  items: T[]
): T[] {
  return items.sort((a, b) => {
    if (isDefined(a.order) && isDefined(b.order)) {
      return a.order - b.order;
    }

    if (isDefined(a.order)) {
      return -1;
    }

    if (isDefined(b.order)) {
      return 1;
    }

    return a.name.localeCompare(b.name);
  });
}

export function formatTagsByGroup(
  tags: TagInclude[] | undefined,
  options: FormatTagGroupOptions = {}
): FormattedTagGroup[] {
  const { sortTags = false, defaultGroupName = "Tags", includeValidTagsFlag = false } = options;

  if (!tags || tags.length === 0) {
    return [];
  }

  const groupedTags = groupBy(tags, (tag) => tag.attributes?.metadata?.filterGroup ?? "other");

  const result = Object.entries(groupedTags).map(([filterGroup, groupTags]) => {
    const groupLabel =
      filterGroup !== "other" && filterGroup ? startCase(filterGroup) : defaultGroupName;

    const tagNamesAndOrder = groupTags
      .filter((tag) => isDefined(tag.attributes?.name))
      .map((tag) => ({
        name: capitalize(tag.attributes.name),
        order: tag.attributes?.metadata?.order,
      }));

    const sortedTags = sortTags
      ? sortTagsByOrderThenAlphabetically(tagNamesAndOrder)
      : tagNamesAndOrder;

    const tagNames = sortedTags.map((tag) => tag.name);

    const formattedTags = new Intl.ListFormat("en", {
      style: "long",
      type: "conjunction",
    }).format(tagNames);

    const baseResult: FormattedTagGroup = { groupLabel, formattedTags };

    if (includeValidTagsFlag) {
      baseResult.hasValidTags = tagNames.length > 0;
    }

    return baseResult;
  });

  return includeValidTagsFlag ? result.filter((group) => group.hasValidTags === true) : result;
}

export function formatTagNamesAsString(
  tags: TagInclude[] | undefined,
  options: { sort?: boolean; separator?: string; abbreviateTags?: boolean } = {}
): string {
  const { sort = false, separator = ", ", abbreviateTags = false } = options;

  if (!tags || tags.length === 0) {
    return "";
  }

  const tagNamesAndOrder = tags
    .map((t) => {
      const label = abbreviateTags ? t.attributes?.metadata?.abbreviation : t.attributes?.name;
      return label ? { name: capitalize(label), order: t.attributes?.metadata?.order } : undefined;
    })
    .filter(isDefined);

  const sortedTags = sort ? sortTagsByOrderThenAlphabetically(tagNamesAndOrder) : tagNamesAndOrder;

  return sortedTags.map((tag) => tag.name).join(separator);
}

export function formatSelectedTagNamesById(
  selectedIds: string[],
  availableTags: TagInclude[],
  options: { sort?: boolean; separator?: string } = {}
): string {
  const { sort = false, separator = ", " } = options;

  if (selectedIds.length === 0 || availableTags.length === 0) {
    return "";
  }

  const tagNamesAndOrder = selectedIds
    .map((id) => {
      const tag = availableTags.find((tag) => tag.id === id);
      return tag?.attributes?.name
        ? {
            name: capitalize(tag.attributes.name),
            order: tag.attributes?.metadata?.order,
          }
        : undefined;
    })
    .filter(isDefined);

  const sortedTags = sort ? sortTagsByOrderThenAlphabetically(tagNamesAndOrder) : tagNamesAndOrder;

  return sortedTags.map((tag) => tag.name).join(separator);
}

export function createTagOptions(tags: TagInclude[]): Array<{ label: string; value: string }> {
  const tagOptionsWithOrder = tags.map((tag) => ({
    label: capitalize(tag.attributes.name),
    value: tag.id,
    name: capitalize(tag.attributes.name),
    order: tag.attributes?.metadata?.order,
  }));

  const sortedTags = sortTagsByOrderThenAlphabetically(tagOptionsWithOrder);

  return sortedTags.map(({ label, value }) => ({ label, value }));
}

export function formatScheduleNotes(schedule: string) {
  return schedule
    .split("\n")
    .map((line) =>
      line
        // Remove common list indicators at the beginning of lines
        .replace(/^\s*[*+•‣⁃→▪▫◦-]\s*/, "")
        // Remove numbered lists (1., 2., etc.)
        .replace(/^\s*\d+[).]\s*/, "")
        // Remove lettered lists (a., b., A), B), etc.)
        .replace(/^\s*[A-Za-z][).]\s*/, "")
        // Remove any remaining bullet-like characters
        .replaceAll("•", "")
        .trim()
    )
    .filter((line) => line.trim() !== "");
}

/* eslint-enable max-lines */
